<script setup>
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTable from "@js/components/tables/DataTable.vue";
import DataTableFilters from "@js/components/tables/DataTableFilters.vue";
import DataTableInformation from "@js/components/tables/DataTableInformation.vue";
import DataTablePageSelector from "@js/components/tables/DataTablePageSelector.vue";
import DataTablePageSizeSelector from "@js/components/tables/DataTablePageSizeSelector.vue";
import DataTableQuickFilters from "@js/components/tables/DataTableQuickFilters.vue";
import DataTableSearchInput from "@js/components/tables/DataTableSearchInput.vue";
import { useResizeObserver } from "@vueuse/core";
import { ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
  title,
  table,
  count,
  globalFilter = "",
  setGlobalFilter,
  availableFilters = [],
  filters = [],
  setFilters = () => {},
  withoutGlobalFilter = false,
  withoutFilters = false,
  withFooter = false,
  withRowNumber = false,
  wrapHeaders = false,
  forceScroll = false,
  renderRowSubComponent = null,
  loading,
  withoutPageSizeSelector = false,
} = defineProps({
  title: {
    type: String,
    required: false,
  },
  table: {
    type: Object,
    required: true,
  },
  count: {
    type: Number,
    required: true,
  },
  globalFilter: {
    type: String,
    required: false,
  },
  setGlobalFilter: {
    type: Function,
    required: true,
  },
  availableFilters: {
    type: [Object, Array],
    required: false,
  },
  filters: {
    type: Array,
    required: false,
  },
  setFilters: {
    type: Function,
    required: false,
  },
  withoutGlobalFilter: {
    type: Boolean,
    required: false,
  },
  withoutFilters: {
    type: Boolean,
    required: false,
  },
  withFooter: {
    type: Boolean,
    required: false,
  },
  withRowNumber: {
    type: Boolean,
    required: false,
  },
  wrapHeaders: {
    type: Boolean,
    required: false,
  },
  forceScroll: {
    type: Boolean,
    required: false,
  },
  renderRowSubComponent: {
    type: Function,
    required: false,
  },
  loading: {
    type: Boolean,
    required: false,
  },
  withoutPageSizeSelector: {
    type: Boolean,
    required: false,
  },
});

const { t } = useI18n();

const dataTableLayout = useTemplateRef("dataTableLayout");

const lastHeight = ref(0);

useResizeObserver(dataTableLayout, (entries) => {
  const entry = entries[0];
  const { height } = entry.contentRect;

  if (height > 0 && !loading) {
    lastHeight.value = height;
  }
});
</script>

<template>
  <div
    v-if="loading"
    class="rounded border border-gray-200 bg-white px-4 py-3"
    :style="{ minHeight: `${lastHeight}px` }"
  >
    <LoadingSpinner />
  </div>

  <div
    v-else
    ref="dataTableLayout"
    class="rounded border border-gray-200 bg-white"
  >
    <div
      v-if="title"
      class="h-14 border-b border-gray-200 px-4 py-3 text-gray-500"
    >
      {{ title }}
    </div>

    <div
      class="flex flex-col-reverse gap-2 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="text-sm">
        {{ t("all.text.total", { total: count }) }}
      </div>

      <div
        v-if="$slots.actions"
        class="flex flex-col items-start gap-2 lg:flex-row lg:justify-end"
      >
        <slot name="actions" />
      </div>
    </div>

    <div
      class="flex flex-col gap-2 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="flex flex-col gap-2 lg:flex-row">
        <DataTableSearchInput
          v-if="!withoutGlobalFilter"
          :table="table"
          :model-value="globalFilter"
          @update:model-value="(newValue) => setGlobalFilter(newValue)"
        />

        <DataTableQuickFilters
          v-if="!withoutFilters && availableFilters.length > 0"
          :filter-definitions="availableFilters"
          :filters="filters"
          :set-filters="setFilters"
        />
      </div>

      <div class="flex flex-col gap-2 lg:flex-row">
        <DataTableFilters
          v-if="!withoutFilters && availableFilters.length > 0"
          :filter-definitions="availableFilters"
          :filters="filters"
          :set-filters="setFilters"
        />

        <DataTablePageSizeSelector v-if="!withoutPageSizeSelector" :table="table" />
      </div>
    </div>

    <DataTable
      v-if="count > 0"
      :table="table"
      :with-footer="withFooter"
      :with-row-number="withRowNumber"
      :wrap-headers="wrapHeaders"
      class="-mt-1"
      :class="{ 'rounded-b': table.getPageCount() === 1 }"
      :force-scroll="forceScroll"
      :render-row-sub-component="renderRowSubComponent"
    />

    <div v-else class="rounded-b bg-gray-50 px-4 py-3">
      {{ t("all.text.noData") }}
    </div>

    <div
      v-if="table.getPageCount() > 1"
      class="flex flex-col gap-2 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
    >
      <DataTableInformation :table="table" :count="count" />

      <DataTablePageSelector :table="table" />
    </div>
  </div>
</template>
